import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const TESTIMONIALS = [
  {
    id: "katherine-miller",
    name: "Katherine Miller",
    source: "Google Review",
    quote:
      "Highly recommend! Quick response, professional, and quality work. We will use Mike again for future gutter needs.",
  },
  {
    id: "ian-l",
    name: "Ian L.",
    source: "Google Review",
    quote:
      "Very professional, arrived on time and did an amazing job. Will definitely use again. Thanks Mike!",
  },
  {
    id: "renae-pearce",
    name: "Renae Pearce",
    source: "Facebook Recommendation",
    quote:
      "Michael did a great job! He arrived and completed the job quickly. He cleaned the gutters and hauled off all the debris. Highly recommend him if you are in need of having your gutters cleaned.",
  },
  {
    id: "tara-turner",
    name: "Tara Turner",
    source: "Google Review",
    quote:
      "He is early for his appt and does a very thorough job. He is thankful for business as well which speaks volumes about his character. He is our go-to guy! Thanks again!",
  },
  {
    id: "kathy-bloedel",
    name: "Kathy Bloedel",
    source: "Google Review",
    quote:
      "Mike did a great job getting my gutters unclogged. He was on time and very personable. Thank you!",
  },
  {
    id: "hollie-lander",
    name: "Hollie Lander",
    source: "Facebook Recommendation",
    quote:
      "Great service! Easy to book and communicates well. Came and got my gutters done in less than 30 minutes! Price was great! Will use him again!",
  },
  {
    id: "mike-google",
    name: "Mike",
    source: "Google Review",
    quote:
      "Arrived fast and was professional. Removed debris from the gutters and hauled the debris off. Will use again.",
  },
  {
    id: "angie-vandevoorde",
    name: "Angie Vandevoorde",
    source: "Google Review",
    quote:
      "Was quick to respond and was able to come the next day. Did a great job and even cleaned up after he was done.",
  },
  {
    id: "morgan-ronk-nelson",
    name: "Morgan Ronk-Nelson",
    source: "Facebook Recommendation",
    quote:
      "Michael did a wonderful job! He was quick and efficient! He communicated well and his prices are super reasonable!",
  },
  {
    id: "brittany-comstock",
    name: "Brittany Comstock",
    source: "Google Review",
    quote:
      "Did an awesome job cleaning out my gutters and man let me tell you they were horrible. Thanks again!",
  },
  {
    id: "andrew-shipley",
    name: "Andrew Shipley",
    source: "Facebook Recommendation",
    quote: "Very quick with what he does!",
  },
];

const ROTATE_MS = 6500;

export function Testimonials() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setTimeout(
      () => setIndex((i) => (i + 1) % TESTIMONIALS.length),
      ROTATE_MS
    );
    return () => clearTimeout(timerRef.current);
  }, [index, paused]);

  const goTo = (next) => {
    setIndex(((next % TESTIMONIALS.length) + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  const t = TESTIMONIALS[index];

  return (
    <section
      className="bg-[#F7F7F5] border-t border-[#E5E5E5] py-12 sm:py-16"
      data-testid="testimonials-section"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#5FBE1F] mb-2">
              <span className="w-6 h-px bg-[#5FBE1F]" />
              What neighbors say
            </div>
            <h2 className="font-display text-2xl sm:text-3xl text-[#000000] leading-tight">
              Real reviews from <span className="text-[#5FBE1F]">real Southwest Iowa neighbors</span>.
            </h2>
          </div>
          <div className="flex items-center gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star
                key={i}
                className="w-5 h-5 text-[#5FBE1F]"
                fill="#5FBE1F"
                strokeWidth={0}
              />
            ))}
            <span className="ml-2 text-sm font-semibold text-[#0A0A0A]">5.0</span>
          </div>
        </div>

        <div
          className="relative bg-white border border-[#E5E5E5] rounded-sm p-6 sm:p-10 min-h-[200px] sm:min-h-[220px] flex"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="testimonials-stage"
        >
          <Quote
            className="absolute top-5 left-5 w-9 h-9 text-[#5FBE1F]/20"
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <AnimatePresence mode="wait">
            <motion.figure
              key={t.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full flex flex-col justify-between gap-6 pl-12 sm:pl-14"
            >
              <blockquote className="text-base sm:text-lg text-[#000000] leading-relaxed">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pt-4 border-t border-[#E5E5E5]">
                <div>
                  <div className="font-display text-lg text-[#000000] leading-none tracking-wide">
                    {t.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#5FBE1F] font-bold mt-1.5">
                    {t.source}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star
                      key={i}
                      className="w-3.5 h-3.5 text-[#5FBE1F]"
                      fill="#5FBE1F"
                      strokeWidth={0}
                    />
                  ))}
                </div>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {TESTIMONIALS.map((tt, i) => (
              <button
                key={tt.id}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Show review ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index
                    ? "w-8 bg-[#5FBE1F]"
                    : "w-3 bg-[#0A0A0A]/15 hover:bg-[#0A0A0A]/30"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#0A0A0A]">
              {String(index + 1).padStart(2, "0")}
              <span className="text-[#9CA09B]"> / {String(TESTIMONIALS.length).padStart(2, "0")}</span>
            </div>
            <button
              type="button"
              onClick={() => goTo(index - 1)}
              aria-label="Previous review"
              data-testid="testimonial-prev"
              className="w-9 h-9 rounded-sm border border-[#0A0A0A]/20 bg-white text-[#0A0A0A] flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              aria-label="Next review"
              data-testid="testimonial-next"
              className="w-9 h-9 rounded-sm border border-[#0A0A0A]/20 bg-white text-[#0A0A0A] flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
